<template>
  <section class="history-section">
    <div class="history-section__inner">
      <div class="history-section__header reveal-child" :style="headerDelay">
        <h2 class="history-section__title">中国民族工商文化发展史</h2>
        <p class="history-section__subtitle">ZHONG GUO MIN ZU GONG SHANG WEN HUA FA ZHAN SHI</p>
      </div>

      <div class="history-section__content reveal-child" :style="accordionDelay">
        <!-- 顶部时间轴：节点均分撑满内容宽度，无横向滚动 -->
        <div v-if="periods.length" class="history-timeline">
          <div class="history-timeline__track">
            <div class="history-timeline__dash history-timeline__dash--start" aria-hidden="true" />
            <div class="history-timeline__line" />
            <div
              class="history-timeline__line history-timeline__line--active"
              :style="activeLineStyle"
            />
            <div class="history-timeline__dash history-timeline__dash--end" aria-hidden="true" />
            <div class="history-timeline__nodes">
              <button
                v-for="(period, index) in periods"
                :key="period.id"
                type="button"
                class="history-timeline__node"
                :class="{
                  'history-timeline__node--active': activeIndex === index,
                  'history-timeline__node--first': index === 0,
                  'history-timeline__node--last': index === periods.length - 1
                }"
                @click="selectPeriod(index)"
              >
                <span class="history-timeline__label">{{ period.title }}</span>
                <span class="history-timeline__range">{{ period.startYear }}</span>
                <span
                  class="history-timeline__dot"
                  :class="{ 'history-timeline__dot--filled': index <= activeIndex }"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>

        <!-- 下部内容区：左侧简介固定，右侧事件列表可横向滚动 -->
        <div
          v-if="activePeriod"
          class="history-panel"
          :style="panelBackgroundStyle"
        >
          <div class="history-panel__overlay" />
          <div class="history-panel__body">
            <aside class="history-panel__intro">
              <h3 class="history-panel__name">{{ activePeriod.title }}</h3>
              <p class="history-panel__desc">
                <span class="history-panel__desc-text">{{ activePeriod.description }}</span>
              </p>
            </aside>

            <div class="history-panel__events-area">
              <div
                ref="eventTrack"
                class="history-panel__events"
                :class="{ 'history-panel__events--dragging': eventDragging }"
                @mousedown="onDragStart"
                @touchstart.passive="onTouchStart"
                @scroll="updateEventScrollState"
              >
                <article
                  v-for="(event, index) in periodEvents"
                  :key="`${event.id || event.title}-${index}`"
                  class="history-event-card"
                  :class="{ 'history-event-card--clickable': event.id }"
                  @click="handleEventClick(event)"
                >
                  <h4 class="history-event-card__title">{{ event.title }}</h4>
                  <div class="history-event-card__image">
                    <img
                      v-if="event.image"
                      :src="event.image"
                      :alt="event.title"
                    />
                    <div v-else class="history-event-card__placeholder">暂无图片</div>
                  </div>
                  <div class="history-event-card__desc">
                    <span class="history-event-card__desc-text">{{ event.description || '暂无描述' }}</span>
                  </div>
                </article>
                <p v-if="!periodEvents.length" class="history-panel__empty">该时期暂无收录事件</p>
              </div>

              <!-- <div class="history-panel__nav">
                <button
                  type="button"
                  class="history-panel__nav-btn"
                  :disabled="!canScrollEventsPrev"
                  aria-label="向左滚动"
                  @click="scrollEvents(-1)"
                >
                  <i class="el-icon-arrow-left" />
                </button>
                <button
                  type="button"
                  class="history-panel__nav-btn"
                  :disabled="!canScrollEventsNext"
                  aria-label="向右滚动"
                  @click="scrollEvents(1)"
                >
                  <i class="el-icon-arrow-right" />
                </button>
              </div> -->
            </div>
          </div>
        </div>

        <p v-else-if="!loading" class="history-section__empty">暂无发展史数据</p>
      </div>
    </div>
  </section>
</template>

<script>
/** HistoryAccordion v2.0 — 顶部时间轴 + 固定简介 + 可滚动事件列表 */
import { fetchHistoryPeriods, mapHistoryPeriod } from '@/api/home'

export default {
  name: 'HistoryAccordion',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    /** 事件列表自动滚动速度，单位 px/s */
    autoScrollSpeed: {
      type: Number,
      default: 40
    },
    /** 手动拖动结束后恢复自动滚动的延迟，单位 ms */
    autoScrollResumeDelay: {
      type: Number,
      default: 5000
    }
  },
  data() {
    return {
      periods: [],
      loading: false,
      activeIndex: 0,
      eventDragging: false,
      dragState: { active: false, moved: false, startX: 0, scrollLeft: 0 },
      canScrollEventsPrev: false,
      canScrollEventsNext: false,
      autoScrollDirection: 1,
      autoScrollPaused: false,
      autoScrollRafId: null,
      autoScrollResumeTimer: null,
      autoScrollLastTime: 0
    }
  },
  computed: {
    headerDelay() {
      return { transitionDelay: this.visible ? '0.1s' : '0s' }
    },
    accordionDelay() {
      return { transitionDelay: this.visible ? '0.3s' : '0s' }
    },
    activePeriod() {
      return this.periods[this.activeIndex] || null
    },
    periodEvents() {
      return (this.activePeriod && this.activePeriod.events) || []
    },
    activeLineStyle() {
      const count = this.periods.length
      const inset = '36px'
      if (count <= 1) {
        return { left: inset, width: `calc(100% - 72px)` }
      }
      const segment = 100 / (count - 1)
      return {
        left: inset,
        width: `calc((100% - 72px) * ${this.activeIndex * segment / 100})`
      }
    },
    panelBackgroundStyle() {
      // const image = this.activePeriod && this.activePeriod.image
      // if (!image) return {}
      // return { backgroundImage: `url(${image})` }
    }
  },
  watch: {
    activeIndex() {
      this.resetEventScroll()
    },
    periodEvents() {
      this.$nextTick(() => {
        this.updateEventScrollState()
        if (!this.autoScrollPaused) this.startAutoScroll()
      })
    }
  },
  mounted() {
    this.loadPeriods()
    document.addEventListener('mousemove', this.onDocumentMove)
    document.addEventListener('mouseup', this.onDocumentUp)
    document.addEventListener('touchmove', this.onDocumentTouchMove, { passive: false })
    document.addEventListener('touchend', this.onDocumentUp)
    window.addEventListener('resize', this.onWindowResize)
  },
  beforeDestroy() {
    this.stopAutoScroll()
    document.removeEventListener('mousemove', this.onDocumentMove)
    document.removeEventListener('mouseup', this.onDocumentUp)
    document.removeEventListener('touchmove', this.onDocumentTouchMove)
    document.removeEventListener('touchend', this.onDocumentUp)
    window.removeEventListener('resize', this.onWindowResize)
  },
  methods: {
    async loadPeriods() {
      this.loading = true
      try {
        const list = await fetchHistoryPeriods()
        this.periods = (Array.isArray(list) ? list : [])
          .map(mapHistoryPeriod)
          .filter(Boolean)
        if (this.activeIndex >= this.periods.length) {
          this.activeIndex = 0
        }
      } catch (e) {
        this.periods = []
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.updateEventScrollState()
          this.startAutoScroll()
        })
      }
    },
    selectPeriod(index) {
      if (this.dragState.moved) return
      this.activeIndex = index
    },
    resetEventScroll() {
      const el = this.$refs.eventTrack
      if (el) el.scrollLeft = 0
      this.autoScrollDirection = 1
      this.clearAutoScrollResumeTimer()
      this.autoScrollPaused = false
      this.$nextTick(() => {
        this.updateEventScrollState()
        this.startAutoScroll()
      })
    },
    onWindowResize() {
      this.updateEventScrollState()
      if (!this.autoScrollPaused) this.startAutoScroll()
    },
    getMaxEventScroll() {
      const el = this.$refs.eventTrack
      if (!el) return 0
      return Math.max(0, el.scrollWidth - el.clientWidth)
    },
    startAutoScroll() {
      if (this.periodEvents.length <= 4) return
      this.stopAutoScrollRaf()
      if (this.autoScrollPaused || this.autoScrollSpeed <= 0) return
      if (!this.getMaxEventScroll()) return
      this.autoScrollLastTime = performance.now()
      this.autoScrollRafId = requestAnimationFrame((t) => this.tickAutoScroll(t))
    },
    stopAutoScrollRaf() {
      if (this.autoScrollRafId != null) {
        cancelAnimationFrame(this.autoScrollRafId)
        this.autoScrollRafId = null
      }
    },
    stopAutoScroll() {
      this.stopAutoScrollRaf()
      this.clearAutoScrollResumeTimer()
    },
    pauseAutoScroll() {
      this.autoScrollPaused = true
      this.stopAutoScrollRaf()
      this.clearAutoScrollResumeTimer()
    },
    scheduleAutoScrollResume() {
      this.clearAutoScrollResumeTimer()
      this.autoScrollResumeTimer = setTimeout(() => {
        this.autoScrollResumeTimer = null
        this.autoScrollPaused = false
        this.startAutoScroll()
      }, this.autoScrollResumeDelay)
    },
    clearAutoScrollResumeTimer() {
      if (this.autoScrollResumeTimer != null) {
        clearTimeout(this.autoScrollResumeTimer)
        this.autoScrollResumeTimer = null
      }
    },
    tickAutoScroll(now) {
      if (this.autoScrollPaused) return
      const el = this.$refs.eventTrack
      if (!el) return

      const maxScroll = this.getMaxEventScroll()
      if (maxScroll <= 0) return

      const dt = (now - this.autoScrollLastTime) / 1000
      this.autoScrollLastTime = now
      let next = el.scrollLeft + this.autoScrollSpeed * this.autoScrollDirection * dt

      if (next >= maxScroll) {
        next = maxScroll
        this.autoScrollDirection = -1
      } else if (next <= 0) {
        next = 0
        this.autoScrollDirection = 1
      }

      el.scrollLeft = next
      this.updateEventScrollState()
      this.autoScrollRafId = requestAnimationFrame((t) => this.tickAutoScroll(t))
    },
    updateEventScrollState() {
      const el = this.$refs.eventTrack
      if (!el) {
        this.canScrollEventsPrev = false
        this.canScrollEventsNext = false
        return
      }
      const maxScroll = el.scrollWidth - el.clientWidth
      this.canScrollEventsPrev = el.scrollLeft > 4
      this.canScrollEventsNext = maxScroll > 4 && el.scrollLeft < maxScroll - 4
    },
    scrollEvents(direction) {
      const el = this.$refs.eventTrack
      if (!el) return
      const card = el.querySelector('.history-event-card')
      const step = card ? card.offsetWidth + 16 : 220
      el.scrollBy({ left: direction * step, behavior: 'smooth' })
      setTimeout(() => this.updateEventScrollState(), 320)
    },
    onDragStart(event) {
      if (event.button != null && event.button !== 0) return
      const el = this.$refs.eventTrack
      if (!el) return
      this.pauseAutoScroll()
      this.dragState.active = true
      this.dragState.moved = false
      this.dragState.startX = event.pageX
      this.dragState.scrollLeft = el.scrollLeft
      this.eventDragging = true
    },
    onTouchStart(event) {
      const touch = event.touches[0]
      if (!touch) return
      this.onDragStart({ pageX: touch.pageX, button: 0 })
    },
    onDocumentMove(event) {
      if (!this.dragState.active) return
      const el = this.$refs.eventTrack
      if (!el) return
      const walk = event.pageX - this.dragState.startX
      if (Math.abs(walk) > 4) this.dragState.moved = true
      el.scrollLeft = this.dragState.scrollLeft - walk
      this.updateEventScrollState()
    },
    onDocumentTouchMove(event) {
      if (!this.dragState.active) return
      event.preventDefault()
      const touch = event.touches[0]
      if (touch) this.onDocumentMove({ pageX: touch.pageX })
    },
    onDocumentUp() {
      if (!this.dragState.active) return
      const wasDragging = this.dragState.active
      const didMove = this.dragState.moved
      this.dragState.active = false
      this.eventDragging = false
      setTimeout(() => {
        this.dragState.moved = false
      }, didMove ? 100 : 0)
      this.updateEventScrollState()
      if (wasDragging) this.scheduleAutoScrollResume()
    },
    handleEventClick(event) {
      if (this.dragState.moved || !event || !event.id) return
      this.$router.push(`/knowledge/shijian/${event.id}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.history-section {
  padding: $spacing-4xl 0;
  background:
    url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"),
    $color-paper;

  &__inner {
    @include page-container;
  }

  &__header {
    margin-bottom: $spacing-2xl;
  }

  &__title {
    font-family: $font-serif;
    font-size: clamp(#{rem(24)}, 3vw, #{rem(36)});
    font-weight: 700;
    color: $color-brown-dark;
    letter-spacing: 3px;
  }

  &__subtitle {
    margin-top: $spacing-sm;
    font-size: rem(12);
    letter-spacing: 3px;
    color: $color-text-light;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: $spacing-xl;
  }

  &__empty {
    text-align: center;
    font-size: rem(14);
    color: $color-text-light;
    padding: $spacing-2xl 0;
  }
}

.history-timeline {
  width: 100%;
  max-width: 100%;
  padding: 0 0 $spacing-base;
  overflow: hidden;

  &__track {
    position: relative;
    width: 100%;
    padding: 0 rem(36);
  }

  &__dash {
    position: absolute;
    bottom: rem(6);
    width: rem(36);
    height: 0;
    border-top: 2px dashed rgba($color-brown-dark, 0.32);
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 1;

    &--start {
      left: 0;
    }

    &--end {
      right: 0;
    }
  }

  &__line {
    position: absolute;
    left: rem(36);
    right: rem(36);
    bottom: rem(6);
    height: 2px;
    background: rgba($color-brown-dark, 0.18);
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 1;

    &--active {
      right: auto;
      background: $color-brown-dark;
      z-index: 1;
      transition: width 0.35s $ease-apple;
    }
  }

  &__nodes {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: stretch;
    width: 100%;
  }

  &__node {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: rem(4);
    padding: 0 rem(4);
    background: none;
    border: none;
    cursor: pointer;

    &--first {
      align-items: flex-start;
      padding-left: 0;

      .history-timeline__label,
      .history-timeline__range {
        text-align: left;
      }
    }

    &--last {
      align-items: flex-end;
      padding-right: 0;

      .history-timeline__label,
      .history-timeline__range {
        text-align: right;
      }
    }

    &--active {
      .history-timeline__label {
        color: $color-brown-dark;
        font-weight: 700;
      }
    }
  }

  &__label {
    font-family: $font-serif;
    font-size: rem(14);
    font-weight: 700;
    color: rgba($color-brown-dark, 0.82);
    line-height: 1.35;
    min-height: calc(#{rem(14)} * 1.35 * 2);
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    /* autoprefixer: ignore next */
    -webkit-box-orient: vertical;
    word-break: break-word;
    transition: color 0.2s;
  }

  &__range {
    font-size: rem(16);
    color: $color-text-light;
    line-height: 1.3;
    min-height: calc(#{rem(11)} * 1.3);
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__dot {
    width: rem(12);
    height: rem(12);
    margin-top: rem(6);
    border-radius: 50%;
    border: 2px solid $color-brown-dark;
    background: transparent;
    box-sizing: border-box;
    flex-shrink: 0;
    transition: background 0.25s $ease-apple;

    &--filled {
      background: $color-brown-dark;
    }
  }
}

.history-panel {
  position: relative;
  min-height: rem(420);
  border-radius: 6px;
  overflow: hidden;
  background-size: cover;
  background-position: center;
  width: 100%;
  max-width: 100%;

  &__overlay {
    position: absolute;
    inset: 0;
    background: rgba($color-cream, 0.88);
    backdrop-filter: blur(1px);
  }

  &__body {
    position: relative;
    z-index: 1;
    display: flex;
    gap: $spacing-xl;
    min-height: rem(420);
    padding: $spacing-xl $spacing-xl $spacing-lg;
    min-width: 0;
  }

  &__intro {
    flex: 0 0 rem(280);
    width: rem(280);
    max-width: 40%;
    min-width: 0;
    align-self: flex-start;
  }

  &__name {
    font-family: $font-serif;
    font-size: clamp(#{rem(26)}, 3vw, #{rem(36)});
    font-weight: 700;
    color: $color-brown-dark;
    margin-bottom: $spacing-base;
    letter-spacing: 2px;
  }

  &__desc {
    margin: 0;
    min-height: 0;
    overflow: hidden;
  }

  &__desc-text {
    font-size: rem(13);
    line-height: 1.9;
    color: rgba($color-brown-dark, 0.88);
    max-height: calc(#{rem(13)} * 1.9 * 4);
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    word-break: break-word;
    /* autoprefixer: ignore next */
    -webkit-box-orient: vertical !important;
  }

  &__events-area {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &__events {
    display: flex;
    flex-wrap: nowrap;
    gap: $spacing-base;
    overflow-x: auto;
    overflow-y: hidden;
    width: 100%;
    padding-bottom: $spacing-xs;
    cursor: grab;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    &--dragging {
      cursor: grabbing;
      user-select: none;
    }
  }

  &__empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: rem(13);
    color: $color-text-light;
    min-width: rem(200);
  }

  &__nav {
    display: flex;
    justify-content: flex-end;
    gap: $spacing-xs;
    margin-top: $spacing-sm;
    padding-right: $spacing-xs;
  }

  &__nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: rem(32);
    height: rem(32);
    background: rgba($color-brown-dark, 0.85);
    color: $color-white;
    border: none;
    border-radius: 4px;
    font-size: rem(14);
    cursor: pointer;
    transition: background 0.2s, opacity 0.2s;

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }

    &:not(:disabled):hover {
      background: $primary-color;
    }
  }
}

.history-event-card {
  flex: 0 0 rem(200);
  width: rem(200);
  display: flex;
  flex-direction: column;
  background: rgba($color-white, 0.96);
  border-radius: 2px;
  overflow: hidden;
  color: $color-brown-dark;
  box-shadow: 0 2px 10px rgba($color-brown-dark, 0.1);

  &--clickable {
    cursor: pointer;
    transition: transform 0.2s $ease-apple, box-shadow 0.2s $ease-apple;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba($color-brown-dark, 0.14);
    }
  }

  &__title {
    padding: $spacing-md $spacing-md $spacing-sm;
    font-family: $font-serif;
    font-size: rem(15);
    font-weight: 700;
    line-height: 1.45;
    flex-shrink: 0;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__image {
    flex-shrink: 0;
    width: 100%;
    aspect-ratio: 4/3;
    overflow: hidden;
    background: $color-cream;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      // filter: grayscale(0.75);
    }
  }

  &__placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: rem(12);
    color: $color-text-light;
  }

  &__desc {
    padding: $spacing-sm $spacing-md $spacing-md;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
  }

  &__desc-text {
    font-size: rem(12);
    line-height: 1.65;
    color: $color-text-light;
    max-height: calc(#{rem(12)} * 1.65 * 4);
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    /* autoprefixer: ignore next */
    -webkit-box-orient: vertical !important;
  }
}

@media (max-width: 960px) {
  .history-panel {
    &__body {
      flex-direction: column;
      min-height: auto;
    }

    &__intro {
      flex: none;
      width: 100%;
      max-width: 100%;
    }
  }

  .history-event-card {
    flex: 0 0 rem(168);
    width: rem(168);
  }
}
</style>
